import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * The body scroll lock is shared between dialogs, because one can close while
 * another is opening — adding to the cart from the quick view closes it and opens
 * the cart drawer. Releasing the lock in that gap and re-applying it a frame later
 * leaves Chromium hit-testing the page as if nothing were there: the new dialog is
 * painted, but no click lands anywhere until the page is reloaded. So count the
 * holders and defer the release, letting the next dialog take over the lock that is
 * already in place.
 */
let scrollLockHolders = 0;
let lockedScrollY = 0;
/** @type {number | null} */
let pendingRelease = null;

const cancelPendingRelease = () => {
  if (pendingRelease === null) return false;

  cancelAnimationFrame(pendingRelease);
  pendingRelease = null;

  return true;
};

/**
 * Freezes the page behind a dialog, remembering where the shopper was.
 */
export function lockBodyScroll() {
  const lockIsStillInPlace = cancelPendingRelease();

  scrollLockHolders++;

  if (scrollLockHolders > 1 || lockIsStillInPlace) return;

  lockedScrollY = window.scrollY;

  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
}

/**
 * Releases the page once the last dialog is gone, back at the remembered position.
 */
export function unlockBodyScroll() {
  scrollLockHolders = Math.max(0, scrollLockHolders - 1);

  if (scrollLockHolders > 0) return;

  cancelPendingRelease();

  // Two frames, so a dialog opening in response to the same event — which happens
  // inside a `requestAnimationFrame` — gets to claim the lock first.
  pendingRelease = requestAnimationFrame(() => {
    pendingRelease = requestAnimationFrame(() => {
      pendingRelease = null;

      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
    });
  });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const open = () => {
      lockBodyScroll();

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    };

    // A dialog that focuses a field on open (the search) must open in the same
    // task as the tap: mobile browsers only show the keyboard for a focus made
    // within the user gesture, and a frame later that gesture is gone.
    if (this.hasAttribute('open-in-gesture')) {
      open();
      return;
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(open);
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    unlockBodyScroll();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true },
);
